const pad = (n: number) => String(n).padStart(2, "0")

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

/** Axis label for a dashboard chart bucket key as the API emits it —
 *  "yyyy-mm-dd" day buckets become "19.5.", "yyyy-mm" month buckets "May 26".
 *  Single owner of the bucket-key format on the client; charts must label from
 *  these keys rather than re-derive dates from their own clock (the server's
 *  window is the source of truth). Unparseable keys pass through unchanged. */
export function formatBucketKey(key: string, granularity: "day" | "month"): string {
  if (granularity === "month") {
    const [year, month] = key.split("-").map((n) => parseInt(n, 10))
    if (!year || !month) {
      return key
    }
    // Non-breaking space so an axis label never wraps between month and year.
    return `${MONTH_NAMES[month - 1]}\u00A0${String(year).slice(2)}`
  }
  const [, mm, dd] = key.split("-")
  return mm && dd ? `${parseInt(dd, 10)}.${parseInt(mm, 10)}.` : key
}

/** Split an ISO timestamp into its local date ("DD.MM.YYYY") and time ("HH:mm")
 *  halves so callers can stack them on two lines. `time` is "" when the input
 *  can't be parsed; `date` then carries the input unchanged (never "Invalid
 *  Date"). This is the single owner of the app's date/time format. */
export function formatDateParts(iso: string): { date: string; time: string } {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return { date: iso, time: "" }
  }
  return {
    date: `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  }
}

/** Format an ISO timestamp as "DD.MM.YYYY, HH:mm" in local time — the single
 *  date/time format used across the app. Returns the input unchanged when it
 *  can't be parsed so callers never render "Invalid Date". */
export function formatDateTime(iso: string): string {
  const { date, time } = formatDateParts(iso)
  return time ? `${date}, ${time}` : date
}

/** Format an instant in an explicit IANA timezone. Scheduling uses this instead
 * of the browser timezone so run evidence matches the schedule's civil clock. */
export function formatDateTimeInTimeZone(iso: string, timeZone: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return iso
  }
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      timeZone,
    }).formatToParts(date)
    const value = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? ""
    return `${value("day")}.${value("month")}.${value("year")}, ${value("hour")}:${value("minute")}`
  } catch {
    return formatDateTime(iso)
  }
}

export function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime()
  if (diffMs < 0) {
    return "just now"
  }
  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 60) {
    return "just now"
  }
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return `${minutes}m ago`
  }
  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    return `${hours}h ago`
  }
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}
